import React, { useState, useCallback } from 'react'
import AdminBuilder from './AdminBuilder'
import UserPreview from './UserPreview'

const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now() + Math.random())

export default function AdminUserQAPlayground() {
  const [questions, setQuestions] = useState([
    {
      id: uid(),
      title: 'Do you agree?',
      type: 'vote',
      layout: 'inline',
      size: 'regular',
      required: true,
      options: [
        { id: uid(), label: 'Yes', icon: 'none' },
        { id: uid(), label: 'No', icon: 'none' }
      ]
    }
  ])

  const [answers, setAnswers] = useState({})

  /* ------------------ QUESTION CRUD ------------------ */

  const updateQuestion = useCallback((id, patch) => {
    setQuestions(qs =>
      qs.map(q => (q.id === id ? { ...q, ...patch } : q))
    )
  }, [])

  const addQuestion = () => {
    setQuestions(qs => [
      ...qs,
      {
        id: uid(),
        title: 'New question',
        type: 'vote',
        layout: 'stack',
        size: 'regular',
        required: true,
        options: [
          { id: uid(), label: 'Option 1', icon: 'none' },
          { id: uid(), label: 'Option 2', icon: 'none' }
        ]
      }
    ])
  }

  const deleteQuestion = id => {
    setQuestions(qs => qs.filter(q => q.id !== id))
    setAnswers(a => {
      const next = { ...a }
      delete next[id]
      return next
    })
  }

  /* ------------------ REORDER SUPPORT ------------------ */

  const reorderQuestions = newOrder => {
    setQuestions(newOrder)
  }

  /* ------------------ OPTION CRUD ------------------ */

  const addOption = id => {
    setQuestions(qs =>
      qs.map(q =>
        q.id === id
          ? {
              ...q,
              options: [
                ...q.options,
                { id: uid(), label: 'New', icon: 'none' }
              ]
            }
          : q
      )
    )
  }

  const updateOption = (qid, oid, label) => {
    setQuestions(qs =>
      qs.map(q =>
        q.id === qid
          ? {
              ...q,
              options: q.options.map(o =>
                o.id === oid ? { ...o, label } : o
              )
            }
          : q
      )
    )
  }

  const deleteOption = (qid, oid) => {
    setQuestions(qs =>
      qs.map(q =>
        q.id === qid
          ? { ...q, options: q.options.filter(o => o.id !== oid) }
          : q
      )
    )
  }

  /* ------------------ ANSWERS ------------------ */

  const vote = (qid, oid) => {
    setAnswers(a => ({
      ...a,
      [qid]: { type: 'vote', optionId: oid }
    }))
  }

  const submitText = (qid, text) => {
    setAnswers(a => ({
      ...a,
      [qid]: { type: 'text', value: text }
    }))
  }

  const clearAnswer = qid => {
    setAnswers(a => {
      const next = { ...a }
      delete next[qid]
      return next
    })
  }

  const isAnswered = q => {
    const a = answers[q.id]
    if (!a) return false
    if (q.type === 'vote') return !!a.optionId
    if (q.type === 'text') return !!a.value?.trim()
    return false
  }

  /* ------------------ RENDER ------------------ */

  return (
    <div className="min-h-screen bg-[#fffdf8] p-10">
      <div className="grid gap-10 lg:grid-cols-2 max-w-8xl mx-auto">
        <AdminBuilder
          questions={questions}
          addQuestion={addQuestion}
          updateQuestion={updateQuestion}
          deleteQuestion={deleteQuestion}
          addOption={addOption}
          updateOption={updateOption}
          deleteOption={deleteOption}
          reorderQuestions={reorderQuestions}
        />

        <UserPreview
          questions={questions}
          answers={answers}
          vote={vote}
          submitText={submitText}
          clearAnswer={clearAnswer}
          isAnswered={isAnswered}
        />
      </div>
    </div>
  )
}